// ============================================================================
// LA FICHE ARTICLE — le détail d'un MODÈLE, en lecture ET en écriture (P10).
//
// ⚠️ ÉDITABLE D'EMBLÉE, SANS BOUTON « MODIFIER » — c'est le relevé de la maquette : aucun bouton
// d'édition, aucun `readOnly`, aucun `disabled` dans ses 1 400 lignes de fiche ; c'est le PIED qui
// porte l'état. Ajouter une bascule inventerait une mécanique que la maquette n'a pas, et créerait un
// second état à tenir alors que le pied dit déjà tout.
//
// ⚠️ MAIS UN CHAMP EST UN CONTRÔLE SI ET SEULEMENT SI LE PRODUCTEUR SAIT L'ÉCRIRE. La maquette rend
// TOUT en `<input>` parce que son « Enregistrer » ne fait que basculer deux booléens — elle n'a rien à
// écrire, donc rien à mentir. Ici un champ engage une écriture réelle : les six champs portés par
// `ArticleInput` sont saisissables, les neuf autres restent du TEXTE LU. Et surtout pas un champ
// désactivé : un contrôle grisé se lit « vous n'avez pas le droit », alors que la vérité est « aucune
// source ne sait l'écrire » (§23.0, interdit n° 6).
//
// ⚠️ LE POINT DUR — MODÈLE ↔ DÉCLINAISON. La fiche décrit un MODÈLE, l'écriture se fait PAR GENCODE.
// Écrire « Famille » sur un modèle de douze déclinaisons, c'est douze événements dans un journal
// append-only. La résolution se fait CÔTÉ SERVEUR (le navigateur ne voit jamais un gencode) et,
// surtout, éditer un champ DIVERGENT (« ≠ ») ÉCRASE la variation : l'écran le dit trois fois, dont une
// fois en bloquant, AVANT l'écriture — c'est une information que seul cet écran détient, et elle
// disparaît au moment du clic.
//
// ⚠️ « ENREGISTRÉ » NE S'ALLUME QU'APRÈS LA RÉPONSE DU SERVEUR, et porte le nombre de déclinaisons
// QU'IL a écrites. La maquette allume son témoin sur un `setState` — c'est le mensonge d'écran type,
// et il ne se porte pas.
// ============================================================================
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ApiError } from '@orgazone/api-envelope/client';
import { dateTime } from '@orgazone/date-format';
import { alert, confirm, Copyable, Field, Icon, Modal, Picker, Tabs, Trans } from '@orgazone/ui-web';
import { api, type ArticleFacet, type ArticleFacets, type ArticleModel, type ArticleModelDetail } from '../api';
import { createFacetValue, createReferential } from './referentiel-facette';
import { key, t } from '../i18n';
import { currentLang } from '../lang';
import { useAuth } from '../auth';
import { insererValeurOfferte } from './article-offres';
import { parseMinor } from '@orgazone/money-format';
import { ChoiceCard, PrixMarge } from './article-prix';
import { preparerPrixAEnvoyer } from './prix-declinaisons';
import { Variants, EtatStock, Divergent } from './articles-shared';
import { EMPTY_VARIANT_DRAFT, draftChanges, draftWithout, draftWork, type VariantDraft } from './articles-variants-axes';
import { PhotosArticle } from './article-photos';
import { StatsArticle } from './article-stats';
import { HistoriqueArticle } from './article-historique';
import { ChampsPerso } from './article-champs-perso';
import { ARTICLE_FLAG_LABELS, ITEM_KIND_TEXT } from '../article-flags';
import { DEFAULT_ITEM_KIND, ITEM_KINDS, isItemKind, isStockableKind, type ItemKind } from '@orgazone/core-catalog';
import { fieldLabel } from '../vocabulary';
import type { VocabularyFieldKey } from '@orgazone/sync-contract';

/**
 * LES ONGLETS SERVIS. ⚠️ Un onglet non servi n'apparaît pas : il ne se grise pas, il n'existe pas.
 * ⚠️ L'ORDRE EST CELUI DE LA MAQUETTE : Général · Déclinaisons · Prix & marge · Stock. Je l'avais inversé
 * (Prix en deuxième) sans le relever — un ordre d'onglets se lit comme une hiérarchie, il ne s'invente pas.
 * ⚠️ LES HUIT SONT SERVIS. Cette note disait le contraire des quatre derniers (« ils n'existent pas »)
 * alors que Photos, Champs perso, Stats et Historique ont chacun leur route depuis longtemps — un
 * commentaire écrit avant eux, jamais relu après. Il envoyait le lecteur chercher un onglet mort.
 */
const ONGLETS = [
  { id: 'general', label: key('Général') },
  { id: 'declinaisons', label: key('Déclinaisons') },
  { id: 'prix', label: key('Prix & marge') },
  { id: 'stock', label: key('Stock') },
  { id: 'photos', label: key('Photos') },
  { id: 'perso', label: key('Champs perso') },
  { id: 'stats', label: key('Stats') },
  { id: 'historique', label: key('Historique') },
] as const;
type OngletId = (typeof ONGLETS)[number]['id'];

/**
 * LES SIX CHAMPS ÉCRIVABLES, dans l'ordre du relevé.
 * ⚠️ MIROIR EXACT de `CHAMPS_FICHE` côté serveur (`catalog/bulk.ts`) : la liste y est FERMÉE et un
 * champ hors liste est refusé. Les deux listes doivent rester identiques — d'où l'ordre repris tel quel.
 */
type ChampEcrivable =
  | 'name' | 'articleCode' | 'modelCode'
  | 'family' | 'subFamily' | 'collection' | 'sector' | 'supplier' | 'brand' | 'season' | 'sizeGrid' | 'sizeGrid2'
  | 'weightG' | 'originCountry' | 'customsCode' | 'composition'
  | 'salePriceInclTaxCents' | 'purchasePriceExclTaxFine' | 'vatRateBp' | 'purchaseRegime'
  | 'isWebActive' | 'isDiscountActive' | 'excludedFromStats' | 'isStockManaged'
  | 'itemKind';

/** Les champs qui se choisissent dans une liste — tout sauf les trois champs de saisie libre. */
type ChampChoix = Exclude<ChampEcrivable,
  'name' | 'articleCode' | 'modelCode' | 'weightG' | 'originCountry' | 'customsCode' | 'composition'
  // ⚠️ Les champs de PRIX ne sont pas des « champs à choix » : ils vivent dans leur propre onglet, avec
  // leurs conversions et leurs tuiles calculées. Les mêler au Classement les ferait rendre comme des
  // pickers de référentiel, ce qu'ils ne sont pas.
  | 'salePriceInclTaxCents' | 'purchasePriceExclTaxFine' | 'vatRateBp' | 'purchaseRegime'
  // ⚠️ LES DEUX GRILLES sont des champs à choix, mais elles ne vivent pas dans le Classement : la
  // maquette les pose dans la barre des Déclinaisons, là où elles commandent les axes de la matrice.
  | 'sizeGrid' | 'sizeGrid2' | ChampDrapeau
  // ⚠️ La NATURE est un choix exclusif à trois cartes, pas un picker de référentiel : elle a sa propre section.
  | 'itemKind'>;

/** Les quatre drapeaux — des interrupteurs, pas des champs. */
type ChampDrapeau = 'isWebActive' | 'isDiscountActive' | 'excludedFromStats' | 'isStockManaged';

/**
 * LES CHAMPS À CHOIX — leur dimension de lecture, et LA TABLE où une valeur nouvelle se crée.
 *
 * ⚠️ `entite` EST CE QUI REND LA CRÉATION RÉELLE : le bouton « + Créer » du picker écrit une ligne dans
 * CETTE table, par le producteur master-data, donc par le journal — elle descendra aux caisses. Sans
 * elle, « créer » ne serait qu'un mot posé sur la valeur tapée.
 *
 * ⚠️ LA SAISON N'A PAS DE TABLE PROPRE : elle vit dans la liste de référence générique (`reference_list`,
 * couple kind+code), là où familles et secteurs ont chacun la leur. La création y écrit donc
 * `kind: 'season'` — c'est la même mécanique, pas une exception silencieuse.
 */
/**
 * ⚠️ LA COLLECTION N'EST PAS DANS CETTE LISTE, et ce n'est pas un oubli : la maquette la pose dans
 * l'IDENTITÉ, à côté du code modèle, et non dans le Classement. Elle reste un champ à CHOIX (avec
 * création) — seul son emplacement diffère, parce qu'elle décrit ce QU'EST le produit et non comment
 * on le range. Sa définition vit donc à son point de rendu.
 *
 * ⚠️ PAS DE COLONNE `label` : le mot du champ se lit AU RENDU, par `fieldLabel(c.champ)` — `champ` est
 * aussi sa clé de vocabulaire. Une colonne qui appellerait `fieldLabel` ici, au chargement du module,
 * figerait le mot de la plateforme avant que le contexte magasin ne soit lu : « Saison » chez le
 * caviste, pour toujours, sans erreur.
 */
// ⚠️ LA CIBLE N'EST PLUS ÉCRITE ICI : elle se dérive de la dimension (`CIBLE_FACETTE`). Cette table
//    portait la BONNE réponse pendant que deux écrans voisins en portaient une fausse — trois copies
//    d'une même règle, dont une cassée. Une règle que plusieurs écrans doivent connaître vit à UN endroit.
const CHOIX: {
  champ: ChampChoix;
  dim: ArticleFacet;
}[] = [
  { champ: 'family', dim: 'fam' },
  { champ: 'subFamily', dim: 'sfam' },
  { champ: 'sector', dim: 'sect' },
  { champ: 'supplier', dim: 'sup' },
  { champ: 'brand', dim: 'marq' },
  { champ: 'season', dim: 'sais' },
];

/**
 * Le poids saisi est-il inacceptable ?
 *
 * ⚠️ MÊME RÈGLE QUE LE SERVEUR, DÉLIBÉRÉMENT : chiffres seuls, rien d'autre. L'écran ne fait pas
 * autorité — le serveur refuse de toute façon — mais il doit refuser POUR LA MÊME RAISON, sinon il
 * laisse passer une saisie qui reviendra en erreur, geste perdu.
 * ⚠️ `null` et l'absence sont VALIDES : détacher un poids est une opération légitime.
 * ⚠️ AU NIVEAU DU MODULE, jamais dans le composant : une règle de validation posée dans un rendu
 * n'est atteignable par aucun test — et un `export` dans un corps de fonction ne compile même pas.
 */
export function poidsSaisiInvalide(v: string | boolean | null | undefined): boolean {
  if (v == null || typeof v === 'boolean') return false;
  const t = v.trim();
  return !/^\d+$/.test(t) || Number(t) > 1_000_000;
}

/**
 * LES CHAMPS MONÉTAIRES DE LA FICHE, LEUR ÉCHELLE ET LEUR MOT — ce que l'écran affiche en euros, ce que
 * le serveur lit en entiers, et le champ que nomme un refus.
 *
 * ⚠️ DEUX ÉCHELLES, ET LES CONFONDRE DÉCALE D'UN FACTEUR 100 : le prix de VENTE est en CENTIMES
 * (2 décimales), le prix d'ACHAT en échelle FINE (4 décimales, imposée par les articles à sous-centime).
 * ⚠️ `vatRateBp` N'EST PAS ICI : son champ est une liste FERMÉE qui porte déjà des points de base
 * (« 2000 »), et `weightG` non plus : il se saisit en grammes entiers, l'unité même que le serveur lit.
 * ⚠️ `field` EST UNE CLÉ, PAS UN MOT : le refus désigne le champ par le mot que l'opérateur a sous les
 * yeux, résolu par `fieldLabel(field)` À L'APPEL. Un mot posé ici serait celui de la plateforme, figé
 * au chargement du module, jamais celui du magasin.
 */
const SHEET_AMOUNTS = {
  salePriceInclTaxCents: { scale: 2, field: 'salePriceInclTax' },
  purchasePriceExclTaxFine: { scale: 4, field: 'purchasePriceExclTax' },
} as const satisfies Record<string, { scale: number; field: VocabularyFieldKey }>;

/**
 * TRADUIT LA SAISIE EN ENTIERS AVANT L'ENVOI — la conversion que le serveur ATTEND et ne fait pas.
 *
 * ⚠️ LE DÉFAUT QUE ÇA FERME (audit 2026-08-25). Les champs de prix envoyaient leur texte BRUT : taper
 * « 40 » écrivait 40 centimes, soit **0,40 €**, sur toutes les déclinaisons et jusque dans les caisses ;
 * et « 39,99 » revenait en « un nombre entier de grammes était attendu ». Le serveur le dit pourtant
 * en toutes lettres (`bulk.ts`, `CHAMPS_NUMERIQUES`) : « L'ARGENT VOYAGE EN ENTIERS… l'écran convertit
 * sa saisie ». Personne ne convertissait.
 * ⚠️ LA CONVERSION PASSE PAR LE CŒUR (`parseMinor` de `@orgazone/money-format`), jamais par un ×100
 * posé à la main : c'est lui qui connaît les séparateurs, les espaces fines d'un copier-coller et
 * l'arrondi de l'échelle.
 * ⚠️ ELLE REND UN MOTIF PLUTÔT QUE DE DEVINER : une saisie illisible arrête l'envoi et se dit à
 * l'écran — un montant faux qui part est irrattrapable, il descend aux caisses.
 */
export function montantsEnEntiers(
  edit: Readonly<Partial<Record<string, string | boolean | null>>>,
): { ok: true; envoi: Record<string, string | boolean | null> } | { ok: false; reason: string } {
  const envoi: Record<string, string | boolean | null> = {};
  for (const [k, v] of Object.entries(edit)) if (v !== undefined) envoi[k] = v;
  for (const [champ, { scale, field }] of Object.entries(SHEET_AMOUNTS)) {
    const v = edit[champ];
    // Absent = non touché ; `null` = détaché volontairement — ni l'un ni l'autre n'est un montant.
    if (v == null || typeof v === 'boolean') continue;
    const lu = parseMinor(v, { scale });
    // ⚠️ UNE CASE VIDÉE N'EST PAS UN DÉTACHEMENT ICI (correctif d'audit 2026-08-25) : ces colonnes sont
    //    `NOT NULL` en base. Envoyer `null` faisait tomber l'écriture sur une erreur Postgres brute,
    //    remontée en 502 — et l'enregistrement étant tout-ou-rien, TOUTE la saisie de la fiche partait
    //    avec. Le refus se dit donc ici, avant l'envoi, et nomme le champ.
    if (lu.state === 'empty') {
      return { ok: false, reason: t('{v} : ce champ ne peut pas être vidé — saisissez un montant, ou laissez-le inchangé.', { v: fieldLabel(field) }) };
    }
    if (lu.state !== 'ok') {
      return { ok: false, reason: t('{v} : « {v2} » n’est pas un montant lisible.', { v: fieldLabel(field), v2: v }) };
    }
    envoi[champ] = String(lu.minor);
  }
  return { ok: true, envoi };
}

/**
 * LA CLÉ DE REJEU D'UNE SOUMISSION HUMAINE.
 *
 * ⚠️ ELLE IDENTIFIE UN ENVOI, PAS UNE INTENTION (défaut du 2026-08-17). Les clés étaient dérivées du
 * CONTENU — « fiche-<modèle>-<champs touchés> », « fam-<famille>-<modèles> ». Or le substrat
 * dédoublonne sur `md:<clé>:<entité>:<clé métier>` : reposer une famille de POLO à MAILLES puis
 * REVENIR à POLO rejouait la clé du premier envoi, l'événement était reconnu comme déjà vu, et
 * L'ÉCRITURE ÉTAIT SILENCIEUSEMENT IGNORÉE — pendant que le pied affichait « Enregistré sur 12
 * déclinaisons ». Un faux succès, le pire des mensonges d'écran (§23.0).
 *
 * ⚠️ REVENIR EN ARRIÈRE EST UNE INTENTION LÉGITIME ET DISTINCTE : on se trompe, on corrige, on
 * re-corrige. Une clé de contenu confond « le même geste » et « le même envoi ».
 *
 * ⚠️ POURQUOI CE N'EST PAS UNE RÉGRESSION DE L'IDEMPOTENCE : elle protège du REJEU D'UN ENVOI (double
 * clic, retransmission réseau), et cet envoi-là garde sa clé jusqu'à sa réponse. Le double clic est
 * d'ailleurs déjà bloqué par l'état « en cours ». Les clés dérivées du CONTENU restent justes là où le
 * même contenu EST le même geste : créer un référentiel, créer une déclinaison sur un gencode donné —
 * relancer un import doit compléter, pas doubler.
 */
function cleDEnvoi(prefixe: string): string {
  return `${prefixe}-${crypto.randomUUID()}`;
}

/**
 * LE MOT DE L'ABSENCE — UN SEUL, PARTOUT (décision utilisateur 2026-08-17).
 *
 * ⚠️ IL Y EN AVAIT SEPT : « non classé », « non renseigné », « non renseignée », « non qualifié »,
 * « aucune », « sans désignation », « sans code article ». Sept mots pour UN état, dont trois qui ne
 * différaient que par un accord. Pire : la rangée du select disait « Non communiqué » et le champ en
 * lecture disait autre chose — le même vide, deux noms selon qu'on regarde ou qu'on modifie.
 *
 * ⚠️ « NON COMMUNIQUÉ » ET PAS « VIDE » : le champ n'est pas vide par accident, il n'a pas été
 * renseigné par la source. C'est le mot de la maquette, et il est juste — il décrit une ABSENCE
 * D'INFORMATION, pas une valeur nulle.
 */
const ABSENT = key('Non communiqué');

export interface ArticleSheetProps {
  /**
   * La LIGNE cliquée, ou `null` quand la fiche est fermée.
   * ⚠️ On reçoit la ligne ENTIÈRE et pas seulement sa clé : l'en-tête se peint immédiatement avec ce
   * qu'on tient déjà en main. Un écran d'attente devant une donnée déjà chargée est une lenteur inventée.
   */
  line: ArticleModel | null;
  /**
   * LES VALEURS OFFERTES, DÉJÀ CHARGÉES PAR L'ÉCRAN HÔTE — le seul moyen d'ouvrir la fiche sans attendre.
   *
   * ⚠️ MESURÉ (2026-08-17) : la fiche redemandait ces valeurs à l'ouverture, ce qui coûtait 761 à 965 ms
   * — À LUI SEUL tout le délai ressenti, ses trois autres lectures faisant 81, 70 et 137 ms. Or la liste
   * les tient DÉJÀ en mémoire (elle en peuple sa barre de filtres) et les passait même à la modale de
   * reclassement. Les lui repasser coûte zéro requête et zéro route nouvelle.
   * ⚠️ NULLABLE, ET C'EST LE REPLI : une fiche montée par un hôte qui n'en a pas (ouverture directe par
   * URL, un jour) les charge elle-même. Le raccourci ne devient jamais une dépendance.
   */
  offresInitiales?: ArticleFacets | null;
  onClose: () => void;
  /** L'écriture a abouti : l'écran hôte relit sa page — ses lignes ont changé. */
  onChanged?: () => void;
}

export function ArticleSheet({ line, offresInitiales = null, onClose, onChanged }: ArticleSheetProps) {
  const { can } = useAuth();
  const [onglet, setOnglet] = useState<OngletId>('general');
  const [sheet, setSheet] = useState<ArticleModelDetail | null>(null);
  /** `disparu` est un état À PART : ce n'est ni une panne, ni une fiche vide. */
  const [failure, setFailure] = useState<{ message: string; disparu: boolean } | null>(null);
  const [attempt, setAttempt] = useState(0);
  /**
   * LA CARTE DE DIFFÉRENCES — seuls les champs TOUCHÉS. Pas un formulaire.
   * ⚠️ C'est elle qui garantit qu'ouvrir la fiche et changer la famille n'aplatit pas au passage la
   * saison divergente d'à côté. Revenir à la valeur d'origine SUPPRIME l'entrée, sinon le pied reste
   * « sale » après un aller-retour qui n'a rien changé.
   */
  const [edit, setEdit] = useState<Partial<Record<ChampEcrivable, string | boolean | null>>>({});
  const [writing, setWriting] = useState(false);
  /** Compte rendu du SERVEUR après écriture — jamais un libellé posé d'avance. */
  const [rendu, setRendu] = useState<{ variants: number; models: number; written: number; duplicates: number } | null>(null);
  const [offres, setOffres] = useState<ArticleFacets | null>(null);

  const modelKey = line?.modelKey ?? null;
  const editable = can('bo.write');

  /**
   * CE QUI SE REMET À ZÉRO EN CHANGEANT D'ARTICLE — et rien d'autre.
   *
   * ⚠️ SÉPARÉ DE LA LECTURE (correctif d'audit 2026-08-25). Ces trois remises à zéro vivaient dans
   * l'effet de lecture, qui dépend AUSSI de `essai` — la relecture déclenchée par un enregistrement les
   * rejouait donc. Le même geste allumait le compte rendu et l'éteignait : « Enregistré sur 12
   * déclinaisons · descendra aux caisses » n'était lisible qu'une fraction de seconde, et la branche
   * AMBRE « rien n'a été réécrit, cet envoi avait déjà été appliqué » — posée exprès comme témoin du faux
   * succès du 2026-08-17 — devenait inatteignable par construction.
   * ⚠️ ET LE RETOUR FORCÉ SUR « Général » AVEC : qui enregistrait depuis Prix & marge, Déclinaisons ou
   * Champs perso était renvoyé sur un autre onglet sans l'avoir demandé, juste après son geste.
   * ⚠️ L'ASYMÉTRIE LE PROUVAIT : `renduPerso` et `renduPrix` n'étaient PAS remis à zéro ici, donc un
   * enregistrement mixte affichait « 3 prix de déclinaison enregistrés » et RIEN sur le reste.
   */
  useEffect(() => {
    if (!modelKey) return;
    setOnglet('general');
    setEdit({});
    setRendu(null);
  }, [modelKey]);

  useEffect(() => {
    if (!modelKey) return;
    let vivant = true;
    setSheet(null);
    setFailure(null);
    api.articleModel(modelKey)
      .then((d) => { if (vivant) setSheet(d); })
      .catch((e: unknown) => {
        if (!vivant) return;
        // ⚠️ « INTROUVABLE » N'EST PAS « EN PANNE » : la liste dont on vient peut dater de plusieurs
        // minutes, et proposer « réessayer » sur un modèle retiré relancerait la même réponse.
        const disparu = e instanceof ApiError && e.status === 404;
        setFailure({ message: e instanceof Error ? e.message : String(e), disparu });
      });
    return () => { vivant = false; };
  }, [modelKey, attempt]);

  /**
   * Les valeurs offertes aux champs à choix.
   * ⚠️ Best-effort ASSUMÉ : sans elles les pickers restent inertes et le DISENT (`unavailable`) — la
   * fiche, elle, s'affiche et se lit.
   * ⚠️ ELLES NE SE RELISENT PLUS APRÈS UNE CRÉATION (correctif d'audit 2026-08-25), et la raison est
   * qu'une relecture NE POUVAIT PAS faire apparaître la valeur créée. Cinq des six dimensions —
   * famille, secteur, fournisseur, marque, saison — sont dérivées des COLONNES TEXTE des articles
   * (`ARTICLE_FACETS`, côté serveur : « l'ensemble des valeurs EST le catalogue, et une valeur
   * inutilisée n'existe pas »). Une famille créée que zéro article ne porte n'apparaîtra donc jamais
   * dans cette liste, ni après la relecture, ni après réouverture de la fiche — seulement quand un
   * article l'aura reçue. On payait un balayage plein catalogue (761 à 965 ms, MESURÉ ligne 180) pour
   * revenir avec exactement la même liste.
   * ⚠️ CE QU'ON FAIT À LA PLACE : on ajoute la valeur que le SERVEUR a retenue, avec un compte de 0.
   * Ce n'est pas une donnée inventée — la création a été confirmée — et le 0 dit la vérité : ce
   * référentiel existe, aucun article ne le porte encore.
   */
  useEffect(() => {
    if (!modelKey || !editable) return;
    // ⚠️ ON NE REDEMANDE PAS CE QU'ON NOUS A DONNÉ. L'hôte a déjà payé cette lecture — 761 à 965 ms
    // mesurés — et rien n'a pu changer entre son chargement et l'ouverture de la fiche.
    // ⚠️ PLUS D'EXCEPTION POUR LES CRÉATIONS : la valeur créée est insérée directement dans le jeu
    //    (`insererValeurOfferte`), une relecture ne l'aurait de toute façon pas fait apparaître.
    if (offresInitiales) { setOffres(offresInitiales); return; }
    let vivant = true;
    api.articleFacets().then((f) => { if (vivant) setOffres(f); }).catch(() => { /* les pickers le diront */ });
    return () => { vivant = false; };
  }, [modelKey, editable, offresInitiales]);

  /**
   * Crée une valeur de référentiel ET l'OFFRE immédiatement au champ qui vient de la demander.
   *
   * ⚠️ LES DEUX SONT LE MÊME GESTE : une création qui ne rejoint pas la liste laisse l'écran afficher
   * un choix où sa propre valeur manque. Les séparer, c'est se garantir qu'un appelant l'oubliera — et
   * le défaut ne se verrait qu'à la réouverture de la fiche.
   * ⚠️ ELLE S'APPELAIT `creerEtOffrir` ET NE RELIT PLUS RIEN (revue 2026-08-25) : le nom promettait un
   * balayage plein catalogue qui, pour cinq dimensions sur six, ne pouvait pas faire apparaître la
   * valeur créée. Un nom qui décrit une mécanique disparue est un commentaire périmé qu'on ne peut pas
   * corriger sans renommer.
   */
  const createAndOffer = useCallback(async (
    entite: string, valeur: string,
    /** La dimension à enrichir — `null` pour ce qui n'est PAS une facette (la grille de tailles). */
    dim: ArticleFacet | null, kind?: string, champ?: 'code' | 'name',
  ) => {
    const retenue = await createReferential(entite, valeur, kind, champ);
    if (dim !== null) setOffres((jeu) => insererValeurOfferte(jeu, dim, retenue));
    return retenue;
  }, []);

  /**
   * Créer la valeur d'une FACETTE, et l'offrir aussitôt.
   * ⚠️ L'APPELANT NE NOMME PLUS L'ENTITÉ, il nomme la DIMENSION. C'est ce qui a permis à deux écrans
   *    voisins d'envoyer « season », qui n'existe pas : chacun portait sa propre correspondance, et
   *    une seule était juste. La table vit désormais dans `referentiel-facette`, et elle est fermée.
   */
  const createFacetAndOffer = useCallback(async (dim: ArticleFacet, valeur: string) => {
    const retenue = await createFacetValue(dim, valeur);
    setOffres((jeu) => insererValeurOfferte(jeu, dim, retenue));
    return retenue;
  }, []);

  /** Pose une valeur, ou l'efface de la carte quand elle revient à son origine. */
  const setChamp = useCallback((champ: ChampEcrivable, v: string | boolean | null, origine: string | boolean | null) => {
    setEdit((prev) => {
      const suivant = { ...prev };
      // ⚠️ L'ÉGALITÉ SE FAIT SUR LA VALEUR NORMALISÉE : `''` et `null` disent la même chose à l'écran
      // (« rien »), et sans ça un champ vidé puis re-vidé resterait « modifié ».
      if ((v ?? '') === (origine ?? '')) delete suivant[champ];
      else suivant[champ] = v;
      return suivant;
    });
    setRendu(null);
  }, []);

  /**
   * LES CHAMPS PERSO EN ATTENTE — même règle que `edit` : rien ne part sans « Enregistrer ».
   * ⚠️ La maquette route les champs perso par le MÊME fDirty que le reste de la fiche (`cfSet` →
   * `setF`) ; un enregistrement automatique au blur divergeait d'elle ET du reste de l'écran
   * (constaté par l'utilisateur, 2026-08-24). `undefined` en valeur = « revenu à l'origine », retiré.
   */
  const [persoEdits, setPersoEdits] = useState<Record<string, string | number | boolean | null>>({});
  const [persoVersion, setPersoVersion] = useState(0);
  /** Le TÉMOIN du dernier enregistrement de champs perso — le pied le dit, comme `rendu` pour le
   *  master-data. ⚠️ Message distinct : les champs perso sont BO-only, « descendra aux caisses »
   *  serait faux pour eux (§23.0). */
  const [renduPerso, setRenduPerso] = useState<number | null>(null);
  const setPerso = useCallback((fieldId: string, v: string | number | boolean | null | undefined) => {
    setPersoEdits((prev) => {
      const suivant = { ...prev };
      if (v === undefined) delete suivant[fieldId];
      else suivant[fieldId] = v;
      return suivant;
    });
    setRenduPerso(null);
  }, []);

  /** Les PRIX PAR DÉCLINAISON en attente (gencode → texte saisi) — même barre que tout le reste
   *  (cohérence exigée par l'utilisateur 2026-08-24 : le bouton local sous le tableau divergeait). */
  const [prixEdits, setPrixEdits] = useState<Record<string, string>>({});
  const [prixVersion, setPrixVersion] = useState(0);
  const [renduPrix, setRenduPrix] = useState<string | null>(null);
  const onPrixEdits = useCallback((suivant: Record<string, string>) => { setPrixEdits(suivant); setRenduPrix(null); }, []);

  /**
   * LES DÉCLINAISONS EN ATTENTE de l'onglet Déclinaisons — couleurs ajoutées, codes scannés —, écrites par le même pied
   * (maquette : `fXCols`/`fEanOv`, écrits par `fSave`, effacés par `fRevert`).
   * ⚠️ ELLES PARTENT APRÈS LES CHAMPS : la grille choisie doit être écrite avant qu'une case de ses tailles naisse.
   */
  const [declDraft, setDeclDraft] = useState<VariantDraft>(EMPTY_VARIANT_DRAFT);
  /** Change après chaque enregistrement : l'onglet relit alors ses déclinaisons écrites. */
  const [declVersion, setDeclVersion] = useState(0);
  const [renduDecl, setRenduDecl] = useState<string | null>(null);
  const onDeclDraft = useCallback((next: VariantDraft) => { setDeclDraft(next); setRenduDecl(null); }, []);

  const touches = Object.keys(edit) as ChampEcrivable[];
  const persoTouches = Object.keys(persoEdits).length;
  const prixTouches = Object.keys(prixEdits).length;
  const declTouches = draftChanges(declDraft);
  const sale = touches.length > 0 || persoTouches > 0 || prixTouches > 0 || declTouches > 0;
  // ⚠️ Une case de prix ILLISIBLE bloque le bouton avec son motif — même règle que le poids.
  const prixIllisibles = preparerPrixAEnvoyer(prixEdits, sheet?.priceMinCents ?? null).invalides.length;
  // ⚠️ UNE SAISIE REFUSÉE BLOQUE LE BOUTON, et le pied DIT laquelle : un « Enregistrer » qui part pour
  // revenir en erreur serveur fait perdre le geste, et un bouton grisé sans motif se lit comme une
  // panne. Même règle que le serveur — ici pour l’expliquer, là-bas pour l’imposer.
  const weightRefused = poidsSaisiInvalide(edit.weightG);
  // ⚠️ MÊME MOTIF QUE LE POIDS : un montant illisible bloque le bouton EN LE DISANT, plutôt que de
  //    partir et de revenir en erreur — un geste perdu, et pour de l'argent.
  const montants = montantsEnEntiers(edit);

  /**
   * LES CHAMPS TOUCHÉS QUI SONT DIVERGENTS — ceux dont l'écriture ÉCRASE une variation réelle.
   * ⚠️ C'est la seule information que cet écran détient et que personne d'autre ne verra : le journal
   * est définitif, et rien ici ne sait restaurer une divergence effacée.
   */
  const divergentsTouches = useMemo(() => {
    if (!sheet) return [] as string[];
    // ⚠️ EXHAUSTIF PAR CONSTRUCTION : `Record<ChampEcrivable, …>` force le compilateur à réclamer
    // chaque nouveau champ écrivable. Un champ oublié ici passerait sans avertissement de divergence —
    // c'est-à-dire écraserait une variation en silence, ce que rien d'autre ne rattraperait.
    const mixte: Record<ChampEcrivable, boolean> = {
      name: sheet.nameMixed, articleCode: false, modelCode: sheet.modelCodeMixed,
      family: sheet.familyMixed, subFamily: sheet.subFamilyMixed, collection: sheet.collectionMixed,
      sector: sheet.sectorMixed, supplier: sheet.supplierMixed, brand: sheet.brandMixed,
      season: sheet.seasonMixed, sizeGrid: sheet.sizeGridMixed, sizeGrid2: sheet.sizeGrid2Mixed,
      weightG: sheet.weightMixed,
      // ⚠️ UN PRIX DIVERGENT EST LE PLUS COÛTEUX À ÉCRASER : c'est de l'argent. La fourchette min≠max
      // EST la divergence — on la traduit ici pour que l'avertissement bloquant parte comme ailleurs.
      salePriceInclTaxCents: sheet.priceMaxCents !== null && sheet.priceMaxCents !== sheet.priceMinCents,
      purchasePriceExclTaxFine: sheet.costMaxFine !== null && sheet.costMaxFine !== sheet.costMinFine,
      vatRateBp: sheet.vatMixed,
      purchaseRegime: sheet.purchaseRegimeMixed,
      originCountry: sheet.originMixed, customsCode: sheet.customsMixed, composition: sheet.compositionMixed,
      isWebActive: sheet.webMixed, isDiscountActive: sheet.discountMixed,
      excludedFromStats: sheet.statsMixed, isStockManaged: sheet.stockManagedMixed,
      itemKind: sheet.itemKindMixed,
    };
    // ⚠️ Les mots des champs se lisent ICI, au rendu, par `fieldLabel` — jamais depuis une table de
    //    module, qui figerait le mot de la plateforme avant que le contexte magasin ne soit lu.
    const nom: Record<ChampEcrivable, string> = {
      name: fieldLabel('name'), articleCode: fieldLabel('articleCode'), modelCode: fieldLabel('modelCode'),
      family: fieldLabel('family'), subFamily: fieldLabel('subFamily'), collection: fieldLabel('collection'),
      sector: fieldLabel('sector'), supplier: fieldLabel('supplier'), brand: fieldLabel('brand'), season: fieldLabel('season'),
      sizeGrid: fieldLabel('sizeGrid'), sizeGrid2: fieldLabel('sizeGrid2'),
      weightG: fieldLabel('weight'),
      salePriceInclTaxCents: fieldLabel('salePriceInclTax'), purchasePriceExclTaxFine: fieldLabel('purchasePriceExclTax'),
      vatRateBp: fieldLabel('vatRate'), purchaseRegime: fieldLabel('purchaseRegime'),
      originCountry: fieldLabel('originCountry'), customsCode: fieldLabel('customsCode'), composition: fieldLabel('composition'),
      isWebActive: t('Diffusé sur le site'), isDiscountActive: t('Remises interdites'),
      // ⚠️ Les deux drapeaux d'exclusion viennent du dictionnaire — trois libellés pour deux
      //    colonnes, c'était déjà un de trop.
      excludedFromStats: t(ARTICLE_FLAG_LABELS.excluded_from_stats!.label),
      isStockManaged: t(ARTICLE_FLAG_LABELS.is_stock_managed!.label),
      itemKind: t('Nature de l’article'),
    };
    return touches.filter((c) => mixte[c]).map((c) => nom[c]);
  }, [sheet, touches]);

  const save = async () => {
    if (!modelKey || !sheet || !sale || writing || weightRefused || !montants.ok || prixIllisibles > 0) return;
    // ⚠️ ON DEMANDE AVANT, JAMAIS APRÈS : la divergence disparaît au moment du clic, et un avertissement
    // rendu ensuite porterait sur une information déjà perdue.
    if (divergentsTouches.length > 0) {
      const ok = await confirm({
        title: t('Écraser des valeurs qui diffèrent ?'),
        message: t('{champs} n’est pas identique sur toutes les déclinaisons de ce modèle ({declinaisons}). Enregistrer posera votre saisie sur toutes. Le journal est définitif : cet écran ne sait pas restaurer la variation effacée.', { champs: new Intl.ListFormat(currentLang(), { type: 'conjunction' }).format(divergentsTouches), declinaisons: t('{n} déclinaison', { n: sheet.variantCount }) }),
        confirmLabel: t('Écraser et enregistrer'),
        tone: 'danger',
      });
      if (!ok) return;
    }
    // ⚠️ LES PRIX SE CONFIRMENT (demande utilisateur 2026-08-24) : c'est de l'argent, et le journal est
    // définitif. La remise au commun d'une case vidée utilise le prix commun de CETTE passe (la saisie
    // en cours de l'onglet Prix si elle existe, sinon le prix servi).
    const communPourEnvoi = (() => {
      const saisi = edit.salePriceInclTaxCents;
      if (typeof saisi === 'string' && saisi.trim()) {
        const lu = parseMinor(saisi, { scale: 2 });
        if (lu.state === 'ok') return lu.minor;
      }
      return sheet.priceMinCents;
    })();
    const envoiPrix = preparerPrixAEnvoyer(prixEdits, communPourEnvoi);
    if (envoiPrix.prices.length > 0) {
      const n = envoiPrix.prices.length;
      const ok = await confirm({
        title: t('Enregistrer les prix par déclinaison ?'),
        message: t('{n} déclinaison va recevoir son prix — une case vidée revient au prix commun. Le journal est définitif ; les caisses recevront ces prix au prochain contact.', { n }),
        confirmLabel: t('Enregistrer les prix'),
      });
      if (!ok) return;
    }
    setWriting(true);
    // ⚠️ UNE DÉCLINAISON ÉCRITE SE RELIT MÊME SI LA SUITE ÉCHOUE : sans relecture, sa case retomberait sur l'ancien état.
    let declWritten = false;
    try {
      if (touches.length > 0) {
        const r = await api.setArticleFields(modelKey, montants.envoi, cleDEnvoi(`fiche-${modelKey}`));
        setEdit({});
        setRendu({ variants: r.variants, models: r.models, written: r.written, duplicates: r.duplicates });
      }
      if (envoiPrix.prices.length > 0) {
        // ⚠️ CLÉ D'ENVOI, pas de contenu : reposer le même prix après un aller-retour est une intention
        // légitime et distincte (défaut du 2026-08-17, corrigé partout).
        const r = await api.setArticleVariantPrices(modelKey, envoiPrix.prices, `px-${crypto.randomUUID()}`);
        setPrixEdits({});
        setRenduPrix(r.written === 0 && r.duplicates > 0
          ? t('Rien n’a été réécrit dans les prix : cet envoi avait déjà été appliqué.')
          : t('{n} prix de déclinaison enregistré · il descendra aux caisses au prochain contact.', { n: r.written }));
        setPrixVersion((v) => v + 1);
      }
      // ⚠️ LES DÉCLINAISONS APRÈS LES CHAMPS ET LES PRIX : la grille choisie est alors écrite, donc une case de ses
      //    tailles peut naître, et un prix saisi visait le code d'AVANT sa correction. Chaque envoi réussi sort du
      //    brouillon tout de suite : un refus plus loin ne laisse en attente que ce qui n'est pas écrit.
      if (declTouches > 0) {
        const work = draftWork(declDraft);
        let created = 0;
        let renamed = 0;
        for (const r of work.renames) {
          await api.renameArticleGencode(r.from, r.to);
          declWritten = true;
          renamed += 1;
          setDeclDraft((d) => draftWithout(d, [r.key]));
        }
        for (const lot of work.creations) {
          // ⚠️ LA CLÉ DE LOT DÉCRIT LE CONTENU, seconde dimension et code compris : un envoi rejoué après une réponse
          //    perdue ne crée rien deux fois, et deux longueurs d'une même taille ne rejouent pas la même écriture.
          const lotKey = `var-${modelKey}-${lot.cells.map((c) => `${c.color ?? ''}|${c.size ?? ''}|${c.size2 ?? ''}|${c.gencode}`).join(',')}`;
          const r = await api.createArticleVariants(modelKey, lot.cells.map(({ color, size, size2, gencode }) => ({ color, size, size2, gencode })), lotKey);
          declWritten = true;
          created += r.created;
          setDeclDraft((d) => draftWithout(d, lot.cells.map((c) => c.key)));
        }
        setDeclDraft((d) => ({ ...d, colors: [] }));
        setRenduDecl([
          created > 0 ? t('{n} déclinaison créée', { n: created }) : null,
          renamed > 0 ? t('{n} code-barres corrigé', { n: renamed }) : null,
        ].filter(Boolean).join(' · ') || null);
      }
      // ⚠️ LES CHAMPS PERSO PARTENT DANS LA MÊME PASSE, champ par champ (leur table est à part du
      // journal master-data). Un refus NOMMÉ (nombre illisible, valeur hors liste…) s'arrête là :
      // la saisie refusée RESTE dans la barre, ce qui a réussi avant est acquis.
      let customWritten = 0;
      for (const [fieldId, v] of Object.entries(persoEdits)) {
        await api.setArticleCustomValue(modelKey, fieldId, v);
        customWritten += 1;
        setPersoEdits((prev) => { const s = { ...prev }; delete s[fieldId]; return s; });
      }
      if (customWritten > 0) setRenduPerso(customWritten);
      setPersoVersion((n) => n + 1);
      // ⚠️ LE SERVEUR SEUL SAIT CE QUI A ÉTÉ ÉCRIT : on relit plutôt que de réafficher la saisie locale
      // comme si elle était la vérité.
      setAttempt((n) => n + 1);
      onChanged?.();
    } catch (e) {
      // La saisie RESTE : l'écran ne fait pas perdre ce qu'il n'a pas su enregistrer.
      await alert({
        title: t('Enregistrement impossible'),
        message: t('{motif} Votre saisie est conservée.', { motif: e instanceof Error ? e.message : String(e) }),
        tone: 'err',
      });
    } finally {
      if (declWritten) setDeclVersion((v) => v + 1);
      setWriting(false);
    }
  };

  const close = () => {
    if (writing) return; // le pied le dit ; `blockClose` refuse déjà
    onClose();
  };

  if (!line) return null;

  // La saisie en cours gagne sur le titre : ce qu on tape s affiche pendant qu on tape.
  const saisiNom = typeof edit.name === 'string' ? edit.name : null;
  const heading = (saisiNom ?? sheet?.name ?? line.name) || line.name;
  const variantes = sheet?.variantCount ?? line.variantCount;
  const actives = sheet?.activeCount ?? line.activeCount;
  const rejouer = () => setAttempt((n) => n + 1);

  return (
    <Modal
      open
      onClose={close}
      // ⚠️ On ouvre une fiche pour la LIRE : saisir « Désignation » d'entrée pose le curseur dans un
      // champ que personne n'a demandé à modifier, et l'écran a l'air d'attendre une frappe.
      autoFocus="panel"
      // ⚠️ LE VOILE NE FERME PAS CETTE FICHE-LÀ (décision utilisateur 2026-08-25). Six onglets, des
      // champs, une position dans la page : le clic à côté n'est jamais volontaire ici — on vise une
      // pastille de coloris, on rate de trois pixels, et tout se referme. La croix, « Fermer » et
      // Échap restent les sorties, toutes trois délibérées.
      closeOnBackdrop={false}
      // ⚠️ POSÉ DANS LA MÊME PASSE QUE LE PREMIER CHAMP, jamais après : dès qu'une saisie existe, une
      // modale qui se ferme sur Échap est une perte de travail silencieuse.
      blockClose={() => {
        if (writing) return true;
        if (!sale) return false;
        void (async () => {
          const n = touches.length + persoTouches + prixTouches + declTouches;
          const ok = await confirm({
            title: t('Fermer sans enregistrer ?'),
            message: t('{n} champ modifié sera perdu.', { n }),
            confirmLabel: t('Fermer sans enregistrer'),
            tone: 'danger',
          });
          if (ok) { setEdit({}); setPersoEdits({}); setPrixEdits({}); setDeclDraft(EMPTY_VARIANT_DRAFT); onClose(); }
        })();
        return true;
      }}
      title={heading}
      icon="tag"
      tone="ink"
      width="hero"
      capHeight="lg"
      density="fiche"
      hero
      meta={<MetaIdentite line={line} sheet={sheet} variantes={variantes} actives={actives} />}
      tabs={(
        <Tabs
          items={ONGLETS.map((o) => ({ ...o, label: t(o.label) }))}
          value={onglet}
          onChange={setOnglet}
          panelId="bo-fiche-article"
          label={t('Sections de la fiche article')}
          size="compact"
        />
      )}
      footer={<Pied sale={sale} writing={writing} rendu={rendu} renduPerso={renduPerso} renduPrix={renduPrix} renduDecl={renduDecl} sheet={sheet} editable={editable}
        bloque={weightRefused ? t('{v} : un nombre entier de grammes est attendu.', { v: fieldLabel('weight') })
          : !montants.ok ? montants.reason
          : prixIllisibles > 0 ? t('Prix par déclinaison : {cases} — un montant s’écrit « 69,99 ».', { cases: t('{n} case illisible', { n: prixIllisibles }) }) : null}
        onCancel={() => { setEdit({}); setPersoEdits({}); setPrixEdits({}); setDeclDraft(EMPTY_VARIANT_DRAFT); setRendu(null); setRenduPerso(null); setRenduPrix(null); setRenduDecl(null); }} onSave={save} onClose={close} />}
    >
      <div id="bo-fiche-article" role="tabpanel" aria-labelledby={`bo-fiche-article-tab-${onglet}`}>
        {onglet === 'general' && (
          <ReadOnlyView sheet={sheet} failure={failure} onRetry={rejouer}>
            {(f) => <General sheet={f} edit={edit} setChamp={setChamp} editable={editable} offres={offres} onCreateFacet={createFacetAndOffer} />}
          </ReadOnlyView>
        )}
        {onglet === 'prix' && (
          <ReadOnlyView sheet={sheet} failure={failure} onRetry={rejouer}>
            {(f) => (
              <PrixMarge
                modelKey={line.modelKey}
                sheet={f}
                edit={edit}
                setChamp={(c, v, o) => setChamp(c as ChampEcrivable, v, o)}
                editable={editable}
                avertir={avertissementDivergence}
                prixEdits={prixEdits}
                onPrixEdits={onPrixEdits}
                prixVersion={prixVersion}
              />
            )}
          </ReadOnlyView>
        )}
        {/* ⚠️ Les déclinaisons ne dépendent PAS de la fiche : elles ont leur propre route, déjà bornée.
            Un échec de la fiche ne doit pas emporter un onglet qui, lui, peut très bien répondre. */}
        {/* ⚠️ LE GRAND LIVRE A SA PROPRE LECTURE, comme Déclinaisons : un échec de la fiche ne doit
            pas emporter un onglet qui, lui, peut très bien répondre. */}
        {onglet === 'stock' && <EtatStock modelKey={line.modelKey} />}
        {onglet === 'photos' && (
          <ReadOnlyView sheet={sheet} failure={failure} onRetry={rejouer}>
            {/* ⚠️ LES CORLORIS VIENNENT DES DECLINAISONS REELLES : une pastille inventee ferait deposer
                une photo sur un coloris qu aucune declinaison ne porte, donc invisible partout. */}
            {(f) => <PhotosArticle modelKey={line.modelKey} couleurs={f.colors} editable={editable} onChanged={onChanged} />}
          </ReadOnlyView>
        )}
        {onglet === 'perso' && <ChampsPerso modelKey={line.modelKey} edits={persoEdits} onEdit={setPerso} version={persoVersion} editable={editable} />}
        {onglet === 'stats' && <StatsArticle modelKey={line.modelKey} />}
        {onglet === 'historique' && <HistoriqueArticle modelKey={line.modelKey} />}
        {onglet === 'declinaisons' && (
          <Variants
            modelKey={line.modelKey}
            sheet
            /* ⚠️ LES DÉCLINAISONS AJOUTÉES OU SCANNÉES ATTENDENT LE MÊME PIED que les champs : rien n'est écrit avant
               « Enregistrer », comme au dessin. Sans droit d'écrire, ni scan ni « Ajouter ». */
            draft={sheet && editable ? { value: declDraft, onChange: onDeclDraft } : undefined}
            colorOffers={offres?.values.coul}
            readVersion={declVersion}
            /* ⚠️ LA GRILLE PASSE PAR LA MÊME CARTE DE DIFFÉRENCES que les champs de l’onglet Général :
               un second chemin d’écriture ferait un second « Enregistrer », et deux pieds qui se
               contredisent. Elle s’enregistre donc avec le reste, par le pied unique de la fiche. */
            grid={sheet ? {
              // ⚠️ CHOISIE ICI, ÉCRITE AU PIED — et la matrice prend ses tailles dès le choix, comme au dessin.
              valeur: ('sizeGrid' in edit ? (edit.sizeGrid as string | null) : sheet.sizeGrid),
              mixte: sheet.sizeGridMixed,
              editable,
              onChange: (v) => setChamp('sizeGrid', v, sheet!.sizeGrid),
              // ⚠️ `null` : une grille de tailles n'est PAS une facette — elle a sa propre route, et
              //    rien à enrichir dans les valeurs offertes.
              onCreate: (nom) => createAndOffer('size_grid', nom, null, undefined, 'name'),
            } : undefined}
            /* ⚠️ LE SECOND AXE EST UN CHAMP DE L'ARTICLE, exactement comme la grille : même carte de
               différences, même pied. Il s'écrivait sur la GRILLE, donc sur tous les modèles qui la
               portent — c'est le renversement §11. */
            grid2={sheet ? {
              valeur: ('sizeGrid2' in edit ? (edit.sizeGrid2 as string | null) : sheet.sizeGrid2),
              mixte: sheet.sizeGrid2Mixed,
              editable,
              onChange: (v) => setChamp('sizeGrid2', v, sheet!.sizeGrid2),
              /* ⚠️ CRÉÉE EN AXE SECONDAIRE, et vide : ses valeurs se saisissent au référentiel. Une
                 grille créée sans son axe deviendrait une grille de TAILLES, offerte au mauvais
                 picker — le producteur fixe l'axe à la création, et ne le change jamais ensuite. */
              onCreate: async (nom) => {
                await api.createReferentielEntry(
                  'size_grid',
                  { name: nom, axis: 'secondary', dimensionLabel: nom, sizes: [] },
                  `grid2:new:${nom}`,
                );
                return nom;
              },
            } : undefined}
          />
        )}
      </div>
    </Modal>
  );
}

/**
 * LE GARDE DES QUATRE ÉTATS, partagé par les onglets qui sortent de la MÊME requête.
 * ⚠️ Partagé et non recopié : deux copies de messages d'échec divergent, et c'est ce qu'on lit quand
 * plus rien ne marche.
 */
function ReadOnlyView({ sheet, failure, onRetry, children }: {
  sheet: ArticleModelDetail | null;
  failure: { message: string; disparu: boolean } | null;
  onRetry: () => void;
  children: (f: ArticleModelDetail) => ReactNode;
}) {
  if (failure?.disparu) {
    return (
      <p role="alert" style={{ margin: 0, padding: '18px 2px', color: 'var(--ink-800)', fontSize: 'var(--t-body)', lineHeight: 1.6 }}>
        
        {t('Ce modèle n’est plus au catalogue de ce magasin. La liste que vous venez de quitter date d’avant son retrait — il n’y a rien à réessayer, fermez et rechargez la liste.')}
      </p>
    );
  }
  if (failure) {
    return (
      <div role="alert" style={{ padding: '18px 2px' }}>
        <p style={{ margin: '0 0 10px', color: 'var(--danger-ink)', fontSize: 'var(--t-body)', fontWeight: 600, lineHeight: 1.6 }}>
          
          {t('La fiche n’a pas pu être lue : {message}', { message: failure.message })}
        </p>
        <p style={{ margin: '0 0 12px', color: 'var(--ink-500)', fontSize: 'var(--t-note)', lineHeight: 1.6 }}>
          
          {t('Rien n’est affiché ci-dessous : ces valeurs viendraient de la base de ce magasin, et elles n’ont pas pu être lues. L’onglet Déclinaisons a sa propre lecture et peut très bien répondre.')}
        </p>
        <button type="button" className="btn-ghost" onClick={onRetry}>{t('Réessayer')}</button>
      </div>
    );
  }
  // ⚠️ « Lecture… » et NON une grille de tirets : un tiret est une absence MESURÉE, pas une attente.
  if (!sheet) {
    return <p style={{ margin: 0, padding: '18px 2px', color: 'var(--ink-500)', fontSize: 'var(--t-body)' }}>{t('Lecture de la fiche…')}</p>;
  }
  return <>{children(sheet)}</>;
}

/**
 * LE PIED — quatre états, jamais fondus.
 * ⚠️ « Enregistré » porte le compte rendu par le SERVEUR, et NOMME l'écart quand des déclinaisons
 * n'ont pas de code-barres : un « Enregistré » sec cacherait précisément le cas que cette fiche existe
 * pour montrer — des déclinaisons qu'aucune écriture ne peut atteindre.
 */
function Pied({ sale, writing, rendu, renduPerso, renduPrix, renduDecl, sheet, editable, bloque, onCancel, onSave, onClose }: {
  sale: boolean;
  writing: boolean;
  rendu: { variants: number; models: number; written: number; duplicates: number } | null;
  /** Champs PERSO écrits au dernier enregistrement — message à part : BO-only, ils ne descendent pas. */
  renduPerso: number | null;
  /** Prix par déclinaison écrits au dernier enregistrement — le message dit sa destination (caisses). */
  renduPrix: string | null;
  /** Déclinaisons créées et codes corrigés au dernier enregistrement — ce que le SERVEUR a écrit. */
  renduDecl: string | null;
  sheet: ArticleModelDetail | null;
  editable: boolean;
  /** Le motif qui empêche d’enregistrer, ou null. ⚠️ UN MOTIF, jamais un simple booléen : « Enregistrer »
   *  inerte sans explication est le pire des deux mondes. */
  bloque: string | null;
  onCancel: () => void;
  onSave: () => void;
  onClose: () => void;
}) {
  const manquantes = sheet && rendu ? sheet.variantCount - rendu.variants : 0;
  return (
    <>
      <span style={{ marginRight: 'auto', display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 'var(--t-body)', fontWeight: 600 }}>
        {writing && <span style={{ color: 'var(--ink-500)' }}>{t('Enregistrement…')}</span>}
        {!writing && sale && (
          <>
            <span aria-hidden style={{ width: 7, height: 7, borderRadius: 'var(--r-pill)', background: bloque ? 'var(--danger)' : 'var(--amber)' }} />
            {/* ⚠️ LE MOTIF PASSE DEVANT : « modifications non enregistrées » est vrai mais inutile quand
                quelque chose empêche précisément de les enregistrer. */}
            <span style={{ color: bloque ? 'var(--danger-ink)' : 'var(--amber-fg)' }}>{bloque ?? t('Modifications non enregistrées')}</span>
          </>
        )}
        {/* ⚠️ « RIEN ÉCRIT » SE DIT, ET SE DIT EN AMBRE. Le substrat reconnaît un envoi déjà appliqué et
            n'écrit pas : sans cette branche, l'écran annonçait « Enregistré sur 12 déclinaisons » alors
            qu'il n'avait rien écrit du tout — le faux succès du 2026-08-17, que rien ne signalait.
            ⚠️ Elle DOIT rester même maintenant que la clé de rejeu est corrigée : elle est le témoin qui
            rendra le prochain défaut du même genre visible en une seconde au lieu d'une heure. */}
        {!writing && !sale && rendu && rendu.written === 0 && rendu.duplicates > 0 && (
          <span style={{ color: 'var(--amber-fg)' }}>
            
            {t('Rien n’a été réécrit : cet envoi avait déjà été appliqué.')}
          </span>
        )}
        {!writing && !sale && rendu && !(rendu.written === 0 && rendu.duplicates > 0) && (
          <span style={{ color: 'var(--ok-fg)' }}>
            {rendu.variants === 0
              ? t('Aucune déclinaison touchée : ce modèle n’a pas de code-barres.')
              : manquantes > 0
                ? t('Enregistré sur {declinaisons} sur {variantCount} — {autres}.', { declinaisons: t('{n} déclinaison', { n: rendu.variants }), variantCount: sheet?.variantCount ?? 0, autres: t('les {n} autres n’ont pas de code-barres, aucune écriture ne peut les atteindre', { n: manquantes }) })
                : t('Enregistré sur {n} déclinaison · descendra aux caisses au prochain contact.', { n: rendu.variants })}
          </span>
        )}
        {/* Le témoin des champs PERSO — s'ajoute à celui du master-data quand les deux ont écrit.
            ⚠️ « sur la fiche », jamais « descendra aux caisses » : les champs perso sont BO-only. */}
        {!writing && !sale && renduPerso !== null && (
          <span style={{ color: 'var(--ok-fg)' }}>
            {t('{n} champ perso enregistré sur la fiche.', { n: renduPerso })}
          </span>
        )}
        {!writing && !sale && renduPrix !== null && (
          <span style={{ color: 'var(--ok-fg)' }}>{renduPrix}</span>
        )}
        {!writing && !sale && renduDecl !== null && (
          <span style={{ color: 'var(--ok-fg)' }}>{renduDecl}</span>
        )}
        {!writing && !sale && !rendu && !editable && (
          <span style={{ color: 'var(--ink-500)', fontWeight: 400 }}>
            
            {t('Lecture seule : votre compte n’a pas le droit d’écrire dans ce magasin.')}
          </span>
        )}
      </span>
      {sale && <button type="button" className="btn-ghost" onClick={() => { if (!writing) onCancel(); }} aria-disabled={writing || undefined}>{t('Annuler')}</button>}
      {sale
        ? <button type="button" className="btn-cta" onClick={() => { if (!writing && !bloque) onSave(); }} aria-disabled={writing || !!bloque || undefined}>{t('Enregistrer')}</button>
        : <button type="button" className="btn-ghost" onClick={onClose}>{t('Fermer')}</button>}
    </>
  );
}

/**
 * L'IDENTITÉ EN BANDE — une ligne d'atomes sous le titre, dans l'en-tête FIXE.
 * ⚠️ QUATRE TRAITEMENTS, PAS UNE RANGÉE DE PASTILLES : le code en chasse fixe nu, le code-barres en
 * pastille pleine, la famille en pilule bordée, le reste en texte. Tout rendre en pastilles effacerait
 * la hiérarchie que la maquette dessine.
 */
function MetaIdentite({ line, sheet, variantes, actives }: {
  line: ArticleModel;
  sheet: ArticleModelDetail | null;
  variantes: number;
  actives: number;
}) {
  const code = sheet?.articleCode ?? line.articleCode;
  const gencode = sheet?.gencode ?? line.gencode;
  const nbCodes = sheet?.gencodeCount ?? line.gencodeCount;
  const sansCode = sheet?.variantsWithoutGencode ?? 0;
  const famille = sheet?.family ?? line.family;
  const familleMixte = sheet?.familyMixed ?? line.familyMixed;

  return (
    <>
      {/* ⚠️ CHASSE FIXE ET DISCRET : c'est un identifiant, il se lit caractère par caractère. Et jamais
          la clé de repli `id:<ulid>`, qui n'est pas un code article mais un identifiant technique. */}
      {code
        ? (
          <Copyable value={code} label={t('Copier le code article {code}', { code })}
            style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>
            {code}
          </Copyable>
        )
        : <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-400)' }}>{t('sans code article')}</span>}

      {/* ⚠️ LE CODE-BARRES QUAND IL Y EN A UN SEUL, l'ICÔNE au-delà : `min(gencode)` élirait le premier
          par ordre alphabétique parmi douze — la même faute que la fourchette de prix réduite à un prix.
          ⚠️ ON NE COMPTE PLUS LES CODES-BARRES À CÔTÉ DES DÉCLINAISONS (décision utilisateur
          2026-08-17) : « 10 codes-barres · 10 déclinaisons » dit deux fois la même chose. L'icône porte
          désormais la notion, le compte de déclinaisons porte le nombre. C'est un écart ASSUMÉ à la
          maquette. */}
      {nbCodes === 1 && gencode
        ? (
          <span style={pastille}>
            <Icon name="barcode" size={13} style={{ flexShrink: 0, color: 'var(--ink-500)' }} />
            <Copyable value={gencode} label={t('Copier le code-barres {gencode}', { gencode })}
              style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-note)', color: 'var(--ink-700)' }}>
              {gencode}
            </Copyable>
          </span>
        )
        : null}

      {famille && <BadgeFamille displayLabel={famille} couleur={sheet?.familyColor ?? null} mixte={familleMixte} />}

      {/* ⚠️ L'ICÔNE ET LE COMPTE NE FONT QU'UN — « une déclinaison = un code-barres » est vrai le plus
          souvent, et c'est ce qui justifie de fusionner les deux mentions.
          ⚠️ MAIS LA BASE PRODUIT L'EXCEPTION : `variantsWithoutGencode` existe précisément parce que des
          déclinaisons N'ONT PAS de code-barres — et celles-là ne peuvent ni être scannées, ni être
          écrites (l'identité d'écriture EST le gencode). Fusionner sans le dire masquerait exactement le
          cas qui compte. Quand l'écart existe, il s'affiche. */}
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>
        <Icon name="barcode" size={13} style={{ flexShrink: 0 }} />
        {t('{n} déclinaison', { n: variantes })}
      </span>

      {/* ⚠️ L'ÉTAT SE JUGE SUR LE GROUPE : « retiré » = AUCUNE déclinaison active. Un modèle dont trois
          tailles sur douze sont retirées reste vendable. */}
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 'var(--t-note)', fontWeight: 600, color: actives === 0 ? 'var(--ink-500)' : 'var(--ok-fg)' }}>
        <span aria-hidden style={{ width: 7, height: 7, borderRadius: 'var(--r-pill)', background: actives === 0 ? 'var(--ink-400)' : 'var(--teal-500)' }} />
        {actives === 0 ? t('Retiré de la vente') : actives === variantes ? t('En vente') : t('{n} en vente', { n: actives })}
      </span>

      {/* ⚠️ CE COMPTE MANQUAIT PARTOUT : une déclinaison sans code-barres n'est atteignable par AUCUNE
          écriture (le producteur écrit par gencode), et les actions de masse la filtrent EN SILENCE. */}
      {sansCode > 0 && (
        <span
          title={t('Ces déclinaisons n’ont pas de code-barres : aucune écriture ne peut les atteindre.')}
          style={{ fontSize: 'var(--t-note)', color: 'var(--amber-fg)', fontWeight: 600 }}
        >
          {t('{sansCode} sans code-barres', { sansCode })}</span>
      )}
    </>
  );
}

/** Pastille pleine — le traitement du code-barres dans la bande d'identité (6 px entre l'icône et le code, comme le dessin). */
const pastille = {
  display: 'inline-flex', alignItems: 'center', gap: 6, height: 22, padding: '0 8px',
  borderRadius: 'var(--r-xs)', background: 'var(--ink-100)', color: 'var(--ink-700)',
  fontFamily: 'var(--font-mono)', fontSize: 'var(--t-note)',
} as const;

/**
 * LE REPÈRE DE FAMILLE — pastille ronde puis libellé, dans une pilule bordée.
 *
 * ⚠️ LA PASTILLE FAIT LE DESSIN, et je l'avais oubliée : sans elle la pilule ressemble à n'importe
 * quelle étiquette. La maquette pose un point de 6 px de la COULEUR DE LA FAMILLE, puis reprend cette
 * couleur pour la bordure et pour l'encre du libellé — c'est un code couleur, pas un ornement.
 *
 * ⚠️ SANS COULEUR, ON NE L'INVENTE PAS. `family.color` existe en base et n'est écrite par personne : la
 * teinter par un hachage du libellé ferait lire au commerçant un code qu'il n'a jamais choisi, et qui
 * changerait au premier renommage. Le repère garde alors sa forme et prend l'encre neutre — le jour où
 * une couleur sera saisie, il la portera sans qu'une ligne ne bouge ici.
 */
function BadgeFamille({ displayLabel, couleur, mixte }: { displayLabel: string; couleur: string | null; mixte: boolean }) {
  const c = couleur ?? 'var(--ink-300)';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, height: 22, padding: '0 9px',
      borderRadius: 'var(--r-pill)', background: 'var(--paper)',
      border: `1px solid ${couleur ?? 'var(--ink-200)'}`,
      fontSize: 'var(--t-note)', fontWeight: 600,
      color: couleur ?? 'var(--ink-800)',
    }}>
      <span aria-hidden style={{ width: 6, height: 6, borderRadius: 'var(--r-pill)', background: c, flexShrink: 0 }} />
      {displayLabel}
      <Divergent on={mixte} />
    </span>
  );
}

/** L'onglet GÉNÉRAL — identité, classement, logistique. */
function General({ sheet, edit, setChamp, editable, offres, onCreateFacet }: {
  sheet: ArticleModelDetail;
  edit: Partial<Record<ChampEcrivable, string | boolean | null>>;
  setChamp: (c: ChampEcrivable, v: string | boolean | null, origine: string | boolean | null) => void;
  editable: boolean;
  offres: ArticleFacets | null;
  /** Créer une valeur de référentiel ET relire les valeurs offertes — les deux sont le même geste. */
  /** Le geste des FACETTES : on nomme la dimension, la cible se dérive. */
  onCreateFacet: (dim: ArticleFacet, valeur: string) => Promise<string>;
}) {
  const logistique = sheet.weightG !== null || sheet.originCountry || sheet.customsCode || sheet.composition;
  const valeur = (c: ChampEcrivable, origine: string | null): string | null =>
    (c in edit ? (edit[c] as string | null) : origine);
  /** Même règle pour un drapeau : la saisie en cours gagne, sinon la valeur du serveur. */
  const valeurBool = (c: ChampDrapeau, origine: boolean): boolean =>
    (c in edit ? Boolean(edit[c]) : origine);
  // ⚠️ LE POIDS EST DU TEXTE TANT QU'IL EST SAISI : le convertir à chaque frappe effacerait la virgule
  // au moment où on la tape, et l'opérateur ne saurait jamais pourquoi son « 1,2 » disparaît.
  const poidsOrigine = sheet.weightG === null ? null : String(sheet.weightG);
  const poidsInvalide = poidsSaisiInvalide(edit.weightG);
  // La nature en cours : la saisie gagne, sinon celle du serveur. ⚠️ AUCUNE quand les déclinaisons divergent ou que la
  // valeur est inconnue : cocher « Marchandise » affirmerait ce que la base ne dit pas.
  const kindEdited = valeur('itemKind', sheet.itemKind);
  const kind: ItemKind | null = 'itemKind' in edit
    ? (isItemKind(kindEdited) ? kindEdited : null)
    : (sheet.itemKindMixed || !isItemKind(sheet.itemKind) ? null : sheet.itemKind);
  /**
   * CE QUE LE GESTE DE NATURE A POSÉ SUR LE STOCK, il le reprend : choisir une prestation sort du stock, revenir à une
   * marchandise rend au stock la valeur d'AVANT ce geste.
   * ⚠️ Sans elle, Marchandise → Prestation → Marchandise laissait « hors suivi » dans l'enregistrement : l'article sortait
   *    du stock sans que personne l'ait demandé. Une valeur posée À LA MAIN avant le geste est rendue telle quelle.
   */
  const stockBeforeKind = useRef<{ had: boolean; value: boolean } | null>(null);
  useEffect(() => { if (!('itemKind' in edit)) stockBeforeKind.current = null; }, [edit]);

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      {/* ⚠️ DEUX RANGÉES EXPLICITES, RELEVÉ DE LA MAQUETTE (l.2851 et l.2868) : « Désignation | Code
          article » en 2fr/1fr, puis « Code modèle | Collection » en 1fr/1fr. La grille automatique
          égalisait tout et poussait la désignation sur sa propre ligne — une désignation et un code
          article n'ont pas à occuper la même largeur. */}
      <Section heading={t('Identité')} libre>
        <Rangee colonnes="minmax(0,2fr) minmax(0,1fr)">
        {editable
          ? (
            <div>
              <Field
                label={fieldLabel('name')}
                value={valeur('name', sheet.name) ?? ''}
                onChange={(e) => setChamp('name', e.target.value || null, sheet.name)}
                pending={'name' in edit}
                hint={sheet.nameMixed ? avertissementDivergence(sheet.variantCount, sheet.name) : undefined}
              />
            </div>
          )
          : <FormRow label={fieldLabel('name')} v={sheet.name} mixed={sheet.nameMixed} vide={t(ABSENT)} />}

        {/* ⚠️ LE CODE ARTICLE EST MODIFIABLE (décision utilisateur), et il est la CLÉ DE MODÈLE : le
            changer DÉPLACE le modèle, et fusionne les deux si le nouveau code en désigne déjà un. Ce
            n'est pas une raison de l'interdire, c'en est une de le DIRE au moment de la saisie — et de
            se ré-ancrer sur la nouvelle clé après l'écriture, ce que fait `onChanged` côté écran. */}
        {editable
          ? (
            <Field
              label={fieldLabel('articleCode')}
              value={valeur('articleCode', sheet.articleCode) ?? ''}
              onChange={(e) => setChamp('articleCode', e.target.value || null, sheet.articleCode)}
              pending={'articleCode' in edit}
              hint={'articleCode' in edit
                ? t('Ce code REGROUPE les déclinaisons en modèle : le changer déplace le modèle entier, et le fusionne avec un modèle existant s’il porte déjà ce code.')
                : t('Regroupe les déclinaisons de ce modèle.')}
            />
          )
          : <FormRow label={fieldLabel('articleCode')} v={sheet.articleCode} mixed={false} vide={t(ABSENT)} />}
        </Rangee>

        <Rangee colonnes="minmax(0,1fr) minmax(0,1fr)">
        {editable
          ? (
            <Field
              label={fieldLabel('modelCode')}
              value={valeur('modelCode', sheet.modelCode) ?? ''}
              onChange={(e) => setChamp('modelCode', e.target.value || null, sheet.modelCode)}
              pending={'modelCode' in edit}
              hint={sheet.modelCodeMixed ? avertissementDivergence(sheet.variantCount, sheet.modelCode) : t('Référence commerciale — ne regroupe rien.')}
            />
          )
          : <FormRow label={fieldLabel('modelCode')} v={sheet.modelCode} mixed={sheet.modelCodeMixed} vide={t(ABSENT)} />}

        {/* ⚠️ LA COLLECTION EST ICI, PAS DANS LE CLASSEMENT (relevé l.2874) : elle décrit ce QU'EST le
            produit — « relie des articles assortis d'une même gamme » — et non la façon dont on le
            range. Elle reste un champ à CHOIX avec création, seul son emplacement change. */}
        {editable
          ? (
            <div>
              <Picker
                id="bo-fiche-collection"
                variant="field"
                label={fieldLabel('collection')}
                clearLabel={t(ABSENT)}
                allLabel={t(ABSENT)}
                options={(offres?.values.coll ?? []).map((v) => ({ value: v.value, label: v.value }))}
                onSearch={(t) => api.articleFacetSearch('coll', t).then((r) => r.map((o) => ({ value: o.value, label: o.value })))}
                onCreate={(v) => onCreateFacet('coll', v)}
                unavailable={!offres}
                value={valeur('collection', sheet.collection) ?? ''}
                onChange={(nv) => setChamp('collection', nv || null, sheet.collection)}
              />
              {sheet.collectionMixed && <Aide>{avertissementDivergence(sheet.variantCount, sheet.collection)}</Aide>}
            </div>
          )
          : <FormRow label={fieldLabel('collection')} v={sheet.collection} mixed={sheet.collectionMixed} vide={t(ABSENT)} />}
        </Rangee>
      </Section>

      <Section heading={t('Classement')}>
        {CHOIX.map((c) => {
          const origine = ORIGINE[c.champ](sheet);
          const mixed = MIXTE[c.champ](sheet);
          if (!editable) return <FormRow key={c.champ} label={fieldLabel(c.champ)} v={origine} mixed={mixed} vide={t(ABSENT)} />;
          const v = valeur(c.champ, origine);
          return (
            <div key={c.champ}>
              <Picker
                id={`bo-fiche-${c.champ}`}
                variant="field"
                label={fieldLabel(c.champ)}
                /* ⚠️ DÉTACHEMENT EXPLICITE : dans un formulaire, « dé-choisir » efface une donnée — il
                   se demande, il ne s'hérite pas du gabarit de filtre. */
                clearLabel={t(ABSENT)}
                allLabel={t(ABSENT)}
                options={(offres?.values[c.dim] ?? []).map((o) => ({ value: o.value, label: o.value }))}
                /* ⚠️ VALEURS EXISTANTES UNIQUEMENT, pas de saisie libre : le référentiel EST le catalogue,
                   et une faute de frappe dans un journal append-only ne se rattrape pas. */
                onSearch={(t) => api.articleFacetSearch(c.dim, t).then((r) => r.map((o) => ({ value: o.value, label: o.value })))}
                /* ⚠️ CRÉER ÉCRIT VRAIMENT : une ligne dans la table de référentiel, par le producteur,
                   donc par le journal — elle descendra aux caisses. C'est ce qui distingue ce bouton
                   d'un « ajouter » qui se contenterait d'accepter la frappe. */
                onCreate={(v) => onCreateFacet(c.dim, v)}
                unavailable={!offres}
                value={v ?? ''}
                onChange={(nv) => setChamp(c.champ, nv || null, origine)}
              />
              {mixed && <Aide>{avertissementDivergence(sheet.variantCount, origine)}</Aide>}
            </div>
          );
        })}
      </Section>

      {/* ⚠️ BLOC ABSENT EN ENTIER si ses quatre colonnes sont vides : quatre absences alignées font
          croire à une fiche incomplète, alors que ce magasin ne renseigne simplement pas ces champs. */}
      {/* ⚠️ LA SECTION EST TOUJOURS LÀ EN ÉCRITURE, et masquée seulement en LECTURE quand elle est vide.
          Un bloc qui disparaît empêche de RENSEIGNER ce qui manque — c'est exactement l'inverse du but. */}
      {(editable || logistique) && (
        <Section heading={t('Logistique & douane')}>
          {/* ⚠️ L'UNITÉ EST DANS L'INTITULÉ, pas en suffixe décoratif : la colonne est un ENTIER de
              grammes, et « poids » seul a déjà été lu en kilos une fois. Le MOT vient du vocabulaire
              du magasin ; l'unité, elle, n'est pas un nom de champ et reste accolée — c'est elle qui
              empêche la relecture en kilos. */}
          {editable
            ? (
              <div>
                <Field
                  label={t('{v} — grammes', { v: fieldLabel('weight') })}
                  value={valeur('weightG', poidsOrigine) ?? ''}
                  onChange={(e) => setChamp('weightG', e.target.value || null, poidsOrigine)}
                  pending={'weightG' in edit}
                  placeholder="N/C"
                  inputMode="numeric"
                  // ⚠️ LE REFUS EST IMMÉDIAT ET NOMMÉ, et il passe par `error` du champ partagé — qui
                  // le dit de TROIS façons (attribut, couleur, texte) et le rattache en
                  // `aria-describedby`. L'écrire à côté ne l'aurait donné qu'à ceux qui le voient.
                  error={poidsInvalide ? t('Un nombre entier de grammes — « 1,2 kg » s’écrit 1200.') : undefined}
                  style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
                />
                {!poidsInvalide && sheet.weightMixed && <Aide>{avertissementDivergence(sheet.variantCount, poidsOrigine)}</Aide>}
              </div>
            )
            : sheet.weightG !== null && (
              <Brut label={fieldLabel('weight')}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontVariantNumeric: 'tabular-nums' }}>
                  {sheet.weightG} g
                  <Divergent on={sheet.weightMixed} />
                </span>
              </Brut>
            )}
          {LIBRES.map((c) => (editable
            ? (
              <div key={c.champ}>
                <Field
                  label={fieldLabel(c.champ)}
                  value={valeur(c.champ, ORIGINE_LIBRE[c.champ](sheet)) ?? ''}
                  onChange={(e) => setChamp(c.champ, e.target.value || null, ORIGINE_LIBRE[c.champ](sheet))}
                  pending={c.champ in edit}
                  style={c.mono ? { fontFamily: 'var(--font-mono)' } : undefined}
                />
                {MIXTE_LIBRE[c.champ](sheet) && <Aide>{avertissementDivergence(sheet.variantCount, ORIGINE_LIBRE[c.champ](sheet))}</Aide>}
              </div>
            )
            : <FormRow key={c.champ} label={fieldLabel(c.champ)} v={ORIGINE_LIBRE[c.champ](sheet)} mixed={MIXTE_LIBRE[c.champ](sheet)} vide={t(ABSENT)} />
          ))}
        </Section>
      )}

      <NatureSection sheet={sheet} kind={kind} editable={editable}
        onChoose={(k) => {
          setChamp('itemKind', k, sheet.itemKind);
          // ⚠️ UNE PRESTATION OU UN TITRE SORT DU SUIVI DE STOCK DANS LE MÊME GESTE : le serveur refuse l'un sans
          //    l'autre, et le dire après coup par un refus laisserait croire que le choix était permis.
          if (!isStockableKind(k)) {
            if (stockBeforeKind.current === null) {
              stockBeforeKind.current = { had: 'isStockManaged' in edit, value: Boolean(edit.isStockManaged) };
            }
            setChamp('isStockManaged', false, sheet.stockManaged);
          } else if (stockBeforeKind.current !== null) {
            const before = stockBeforeKind.current;
            stockBeforeKind.current = null;
            setChamp('isStockManaged', before.had ? before.value : sheet.stockManaged, sheet.stockManaged);
          }
        }}
      />

      <SectionCartes heading={t('Diffusion & commerce')}>
        <CarteInterrupteur
          displayLabel={t('Diffusé sur le site')}
          aide={t('Publié sur la boutique en ligne.')}
          actif={valeurBool('isWebActive', sheet.webActive)}
          mixte={sheet.webMixed}
          editable={editable}
          onToggle={(v) => setChamp('isWebActive', v, sheet.webActive)}
        />
        {/* ⚠️ L’INTERRUPTEUR EST L’INVERSE DE LA COLONNE, et c’est le libellé qui commande : l’écran dit
            « interdites », la base dit « remisable ». La maquette l’écrit noir sur blanc — « par défaut,
            tout est remisable : on interdit, on n’autorise pas ». L’inversion se fait ICI, une seule fois. */}
        <CarteInterrupteur
          displayLabel={t('Remises interdites')}
          aide={t('Aucune remise, promo ou solde ne touche cet article. Par défaut, tout est remisable — on interdit, on n’autorise pas.')}
          actif={!valeurBool('isDiscountActive', sheet.discountable)}
          mixte={sheet.discountMixed}
          editable={editable}
          tone="amber"
          onToggle={(v) => setChamp('isDiscountActive', !v, sheet.discountable)}
        />
      </SectionCartes>

      <SectionCartes heading={t('Exclusions')}>
        {/* ⚠️ PHRASE ET AIDE VIENNENT DU DICTIONNAIRE PARTAGÉ (`article-flags`) : écrites ici, elles
            avaient déjà divergé de l'historique, qui nommait le même réglage à l'envers. */}
        <CarteInterrupteur
          displayLabel={t(ARTICLE_FLAG_LABELS.excluded_from_stats!.label)}
          aide={t(ARTICLE_FLAG_LABELS.excluded_from_stats!.help)}
          actif={valeurBool('excludedFromStats', sheet.excludedFromStats)}
          mixte={sheet.statsMixed}
          editable={editable}
          tone="amber"
          cadre
          onToggle={(v) => setChamp('excludedFromStats', v, sheet.excludedFromStats)}
        />
        {/* La colonne stocke « suivi », la carte dit « hors suivi » : l'inversion est déclarée dans le
            dictionnaire, elle n'est pas décidée ici. */}
        <CarteInterrupteur
          displayLabel={t(ARTICLE_FLAG_LABELS.is_stock_managed!.label)}
          aide={t(ARTICLE_FLAG_LABELS.is_stock_managed!.help)}
          actif={!valeurBool('isStockManaged', sheet.stockManaged)}
          mixte={sheet.stockManagedMixed}
          // ⚠️ VERROUILLÉ HORS SUIVI pour une prestation ou un titre : le remettre en stock serait refusé. Nature inconnue ou
          //    mêlée : libre, et le serveur dit ce qu'il refuse.
          editable={editable && (kind === null || isStockableKind(kind))}
          tone="amber"
          cadre
          onToggle={(v) => setChamp('isStockManaged', !v, sheet.stockManaged)}
        />
      </SectionCartes>

      {/* ⚠️ L’ENCART EST DANS LA MAQUETTE, mot pour mot : il dit la seule chose qu’un gérant risque de
          comprendre de travers — un article exclu des statistiques COMPTE TOUJOURS dans le chiffre
          d’affaires. Le résumer le viderait de son sens. */}
      <p style={{
        margin: 0, padding: '13px 15px', borderRadius: 'var(--r-sm)', background: 'var(--ink-50)',
        border: '1px solid var(--ink-200)', fontSize: 'var(--t-body)', color: 'var(--ink-600)', lineHeight: 1.6,
      }}>
        <Trans k='Les exclusions servent aux <1>articles divers</1> (un « divers 10 € » générique) et aux <2>produits non marchands</2> (sac imprimé, échantillon). Un article exclu des statistiques <3>compte toujours dans le chiffre d’affaires</3> — une vente reste une vente : seules les analyses produit l’écartent, pour ne pas polluer les meilleures ventes ni l’indice de vente.' parts={[(x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>, (x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>, (x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>]} /></p>

      <p style={{ margin: 0, fontSize: 'var(--t-meta)', color: 'var(--ink-400)', lineHeight: 1.6 }}>
        {sheet.createdAt ? t('Créé le {v}', { v: dateTime(sheet.createdAt) }) : t('Date de création inconnue')}
        {' · '}
        {/* ⚠️ « Dernière écriture », JAMAIS « Modifié le » : `updated_at` est le curseur de delta de la
            sync — une descente de catalogue le réécrit sur toute la base. */}
        {sheet.updatedAt ? t('dernière écriture le {v}', { v: dateTime(sheet.updatedAt) }) : t('aucune écriture datée')}
      </p>

      {/* ⚠️ LE PARAGRAPHE « CE QUI NE S'ÉCRIT PAS » A ÉTÉ RETIRÉ (audit 2026-08-25). Il annonçait que le
          poids ne se saisit pas, que la diffusion web, les remises interdites, les exclusions et les
          champs personnalisés « ne figurent pas », et que photos, historique et statistiques « n'ont
          pas encore de données » — les quatre affirmations étaient fausses, et ce qu'elles niaient était
          rendu quelques dizaines de pixels plus haut ou dans un onglet voisin. Un gérant qui bascule
          « Remises interdites » puis lit que ce réglage n'existe pas ne sait plus si son geste a été
          écrit ; sur une page qui alimente un journal append-only, c'est exactement le doute que §23.0
          existe pour supprimer. Il n'a pas été réécrit : il ne restait rien de vrai à dire. */}
    </div>
  );
}

/**
 * L’onglet PRIX & MARGE vit dans son PROPRE fichier (article-prix.tsx).
 *
 * ⚠️ SÉPARÉ parce qu’il ne partage RIEN avec le reste de la fiche : ses champs sont des montants (deux
 * échelles d’entiers, conversion à la saisie), ses valeurs affichées sont CALCULÉES, et son régime
 * d’achat inverse le sens des champs. Mêlé aux sections de texte, il aurait fait grossir un fichier
 * déjà long sans qu’aucune ligne ne serve deux fois.
 */

/**
 * Les champs de LOGISTIQUE en saisie libre — un identifiant se rend en chasse fixe.
 * ⚠️ Pas de colonne `label`, pour la même raison que `CHOIX` : le mot se lit au rendu par
 *    `fieldLabel(c.champ)`, jamais au chargement du module.
 */
type ChampLibre = 'originCountry' | 'customsCode' | 'composition';
const LIBRES: { champ: ChampLibre; mono?: boolean }[] = [
  { champ: 'originCountry' },
  { champ: 'customsCode', mono: true },
  { champ: 'composition' },
];
const ORIGINE_LIBRE: Record<ChampLibre, (f: ArticleModelDetail) => string | null> = {
  originCountry: (f) => f.originCountry, customsCode: (f) => f.customsCode, composition: (f) => f.composition,
};
const MIXTE_LIBRE: Record<ChampLibre, (f: ArticleModelDetail) => boolean> = {
  originCountry: (f) => f.originMixed, customsCode: (f) => f.customsMixed, composition: (f) => f.compositionMixed,
};

/**
 * OÙ LIRE CHAQUE CHAMP À CHOIX, et son marqueur de divergence.
 * ⚠️ Deux tables plutôt qu'une cascade de ternaires : la cascade se lisait de moins en moins à chaque
 * champ ajouté, et son dernier `else` avalait silencieusement tout champ oublié.
 */
const ORIGINE: Record<ChampChoix, (f: ArticleModelDetail) => string | null> = {
  family: (f) => f.family, subFamily: (f) => f.subFamily, collection: (f) => f.collection,
  sector: (f) => f.sector, supplier: (f) => f.supplier, brand: (f) => f.brand, season: (f) => f.season,
};
const MIXTE: Record<ChampChoix, (f: ArticleModelDetail) => boolean> = {
  family: (f) => f.familyMixed, subFamily: (f) => f.subFamilyMixed, collection: (f) => f.collectionMixed,
  sector: (f) => f.sectorMixed, supplier: (f) => f.supplierMixed, brand: (f) => f.brandMixed,
  season: (f) => f.seasonMixed,
};


/** La phrase qui dit ce qu'une écriture va écraser — la même partout, pour qu'elle ne dérive pas. */
// ⚠️ DEUX PHRASES ENTIÈRES, PAS UNE PHRASE À MORCEAU CONDITIONNEL. La première écriture glissait
//    « — « X » est celle affichée » dans un trou : le traducteur recevait ce bout SANS la phrase qui
//    l'accueille, et sa place dans l'ordre des mots change d'une langue à l'autre.
/**
 * LA NATURE DE L'ARTICLE — marchandise ou prestation : deux cartes exclusives.
 * ⚠️ LA NATURE « TITRE » NE SE CHOISIT PAS (décision utilisateur) : un chèque cadeau est un titre, vendu en caisse par son
 *    bouton, jamais un article. La seule fiche qui la porte est l'article par lequel l'ancien logiciel vendait ses
 *    chèques cadeaux, que la REPRISE marque et archive ; la fiche la montre alors en lecture, avec son explication.
 */
const CHOOSABLE_KINDS: readonly ItemKind[] = ITEM_KINDS.filter((k) => k !== 'title');

function NatureSection({ sheet, kind, editable, onChoose }: {
  sheet: ArticleModelDetail; kind: ItemKind | null; editable: boolean; onChoose: (k: ItemKind) => void;
}) {
  const heading = t('Nature de l’article');
  const shown = sheet.itemKind ? t(ITEM_KIND_TEXT[sheet.itemKind].label) : null;
  return (
    <Section heading={heading} libre>
      {editable && kind !== 'title'
        ? (
          <div role="radiogroup" aria-label={heading} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {CHOOSABLE_KINDS.map((k, i) => (
              <ChoiceCard key={k} index={i} count={CHOOSABLE_KINDS.length} pick={(j) => onChoose(CHOOSABLE_KINDS[j] ?? DEFAULT_ITEM_KIND)}
                checked={kind === k} anyChecked={kind !== null} heading={t(ITEM_KIND_TEXT[k].label)} text={t(ITEM_KIND_TEXT[k].help)} onChoose={() => onChoose(k)} />
            ))}
          </div>
        )
        : <FormRow label={heading} v={shown} mixed={sheet.itemKindMixed} vide={t(ABSENT)} />}
      {kind === 'title' && <Aide>{t(ITEM_KIND_TEXT.title.help)}</Aide>}
      {/* ⚠️ La valeur nommée est celle COCHÉE : tant que rien ne l'est, la phrase n'en nomme aucune. */}
      {editable && sheet.itemKindMixed && <Aide>{avertissementDivergence(sheet.variantCount, kind === null ? null : t(ITEM_KIND_TEXT[kind].label))}</Aide>}
    </Section>
  );
}

function avertissementDivergence(variantes: number, displayed: string | null): string {
  // ⚠️ DEUX DÉCLINAISONS AU MOINS PAR CONSTRUCTION — une divergence en exige deux : le singulier du catalogue est écrit
  //    identique au pluriel, c'est une décision et non un oubli (Forme DF).
  return displayed
    ? t('{n} déclinaisons ne portent pas toutes la même valeur — « {displayed} » est celle affichée. Enregistrer posera votre saisie sur toutes.', { n: variantes, displayed })
    : t('{n} déclinaisons ne portent pas toutes la même valeur. Enregistrer posera votre saisie sur toutes.', { n: variantes });
}

/** Une aide sous un champ — même encre et même métrique que celle de `Field`, pour ne pas décaler. */
function Aide({ children }: { children: ReactNode }) {
  return <p style={{ margin: '5px 0 0', fontSize: 'var(--t-note)', color: 'var(--amber-fg)', lineHeight: 1.5 }}>{children}</p>;
}

/**
 * UNE SECTION — un surtitre discret, puis une GRILLE de champs.
 * ⚠️ NUE, sans carte ni filets internes : la maquette n'encadre rien dans cet onglet. Un cadre par
 * section découpe la fiche en quatre objets alors qu'elle en décrit un seul.
 * ⚠️ `align-items: start` est INDISPENSABLE dès qu'un champ porte une aide ou un encart : sans lui,
 * toute la rangée s'étire à la hauteur du plus haut.
 */
/**
 * UNE RANGÉE DE CHAMPS aux largeurs DÉCLARÉES.
 * ⚠️ Existe parce que la maquette compose l'identité en rangées INÉGALES : la grille automatique
 * égalise, or une désignation mérite deux fois la largeur d'un code article. Sur écran étroit, les
 * colonnes retombent l'une sous l'autre — d'où le `minmax(0, …)`, sans lequel un champ long
 * refuserait de se réduire et ferait défiler la modale horizontalement.
 */
function Rangee({ colonnes, children }: { colonnes: string; children: ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: colonnes, gap: 13, alignItems: 'start' }}>
      {children}
    </div>
  );
}

function Section({ heading, children, libre = false }: {
  heading: string;
  children: ReactNode;
  /**
   * ⚠️ `libre` REND LA MISE EN PAGE AUX ENFANTS. La grille automatique
   * (`auto-fill minmax(210px,1fr)`) convient à une LISTE de champs équivalents — Classement,
   * Logistique. Elle ne convient pas à l'Identité, que la maquette compose en RANGÉES EXPLICITES de
   * largeurs inégales (2fr/1fr puis 1fr/1fr) : une désignation et un code article n'ont pas à occuper
   * la même largeur, et l'automatique les égalise.
   */
  libre?: boolean;
}) {
  return (
    <section>
      <h3 style={{
        margin: '0 0 12px', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)',
        fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--ink-500)',
      }}>{heading}</h3>
      {libre
        ? <div style={{ display: 'grid', gap: 13 }}>{children}</div>
        : (
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))',
            gap: '13px 20px', alignItems: 'start',
          }}>
            {children}
          </div>
        )}
    </section>
  );
}

/**
 * UN CHAMP LU : intitulé AU-DESSUS, valeur alignée à GAUCHE.
 * ⚠️ Sa métrique d'intitulé suit celle de `Field` (11,5 / 500 / marge 5) : sans ça, un champ lu et un
 * champ saisi côte à côte se décalent d'un pixel et la grille se lit de travers.
 */
function Brut({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 'var(--t-note)', fontWeight: 500, color: 'var(--ink-500)', marginBottom: 5 }}>{label}</div>
      <div style={{ fontSize: 'var(--t-lead)', fontWeight: 600, color: 'var(--ink-900)', display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
        {children}
      </div>
    </div>
  );
}

/**
 * Un champ TEXTE lu, avec son marqueur de divergence et son mot pour le vide.
 * ⚠️ LE VIDE SE DIT PAR UN MOT, pas par un tiret : sur une grille de champs larges, un « — » isolé sous
 * un intitulé se lit comme une valeur tronquée plutôt que comme une absence.
 */
function FormRow({ label, v, mixed, vide }: { label: string; v: string | null; mixed: boolean; vide: string }) {
  return (
    <Brut label={label}>
      {v
        ? (
          <>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{v}</span>
            <Divergent on={mixed} />
          </>
        )
        : <Vide>{vide}</Vide>}
    </Brut>
  );
}

/** Une absence, dite avec un mot et en encre effacée — jamais confondue avec une valeur. */
function Vide({ children, title }: { children: ReactNode; title?: string }) {
  return <span title={title} style={{ fontWeight: 400, color: 'var(--ink-400)' }}>{children}</span>;
}

/**
 * UNE SECTION DE CARTES — surtitre, puis une grille de cartes qui se réagencent.
 * ⚠️ `auto-fit` avec un plancher de 238 px : c'est le relevé. Deux colonnes sur un panneau de 920,
 * une seule quand la modale passe en pleine page — sans point de rupture à tenir à la main.
 */
function SectionCartes({ heading, children }: { heading: string; children: ReactNode }) {
  return (
    <section>
      <h3 style={{
        margin: '0 0 10px', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)',
        fontWeight: 400, letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink-500)',
      }}>{heading}</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(238px, 1fr))', gap: 11 }}>
        {children}
      </div>
    </section>
  );
}

/**
 * UNE CARTE-INTERRUPTEUR — la carte ENTIÈRE est le bouton, pas seulement le petit rail.
 *
 * ⚠️ TOUTE LA CARTE BASCULE, et c'est le relevé : viser un rail de 38 × 22 px demande de la précision
 * pour un réglage qui n'en mérite pas. La cible fait donc 238 px de large.
 *
 * ⚠️ LE TON AMBRE N'EST PAS DÉCORATIF : il marque les réglages qui RETIRENT quelque chose (les remises,
 * les statistiques, le stock). Un article ainsi réglé sort d'un mécanisme que tout le monde croit
 * universel — et c'est précisément ce qu'on ne doit pas découvrir six mois plus tard en cherchant
 * pourquoi les chiffres ne tombent pas juste.
 *
 * ⚠️ EN LECTURE SEULE, LA CARTE N'EST PAS UN BOUTON. Un bouton qui refuse de basculer se lit « en
 * panne » ; un état rendu se lit pour ce qu'il est.
 */
function CarteInterrupteur({ displayLabel, aide, actif, mixte, editable, tone, cadre, onToggle }: {
  displayLabel: string;
  aide: string;
  actif: boolean;
  mixte: boolean;
  editable: boolean;
  /** `amber` pour un réglage qui retire quelque chose ; défaut = la teinte de marque. */
  tone?: 'amber';
  /** Encadre la carte en ambre en permanence (les Exclusions), pas seulement quand elle est active. */
  cadre?: boolean;
  onToggle: (v: boolean) => void;
}) {
  const teinte = tone === 'amber' ? 'var(--amber-fg)' : 'var(--teal-500)';
  const corps = (
    <>
      {/* Le rail et son pouce — 38 × 22, pouce de 16, exactement le relevé. */}
      <span aria-hidden style={{
        width: 38, height: 22, borderRadius: 'var(--r-pill)', flexShrink: 0, display: 'flex', alignItems: 'center',
        justifyContent: actif ? 'flex-end' : 'flex-start', padding: '0 3px',
        background: actif ? teinte : 'var(--ink-200)',
        transition: 'background .13s',
      }}>
        <span style={{ width: 16, height: 16, borderRadius: 'var(--r-pill)', background: '#fff' }} />
      </span>
      <span style={{ minWidth: 0 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 'var(--t-lead)', fontWeight: 600, color: 'var(--ink-900)' }}>
          {displayLabel}
          {/* ⚠️ « certaines oui, certaines non » est un ÉTAT RÉEL sur douze déclinaisons : basculer
              l'interrupteur tranchera pour toutes, et l'écran doit le dire avant. */}
          <Divergent on={mixte} />
        </span>
        <span style={{ display: 'block', fontSize: 'var(--t-note)', color: 'var(--ink-600)', lineHeight: 1.45, marginTop: 2 }}>
          {aide}
        </span>
      </span>
    </>
  );

  const socle = {
    display: 'flex', alignItems: 'center', gap: 11, padding: '12px 14px',
    borderRadius: 'var(--r-sm)', textAlign: 'left' as const,
    border: `1px solid ${cadre ? 'var(--amber)' : 'var(--ink-200)'}`,
    background: cadre ? 'var(--amber-bg)' : 'var(--paper)',
  };

  if (!editable) return <div style={socle}>{corps}</div>;
  return (
    <button type="button" role="switch" aria-checked={actif} onClick={() => onToggle(!actif)} style={{ ...socle, cursor: 'pointer' }}>
      {corps}
    </button>
  );
}
